import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ImagePlus, X } from "lucide-react";
import api, { errText, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", price: "", sku: "", description: "", image_url: "", stock_status: "in_stock", faqs: "", photos: [] };
const wc = (t) => ((t || "").trim() ? t.trim().split(/\s+/).length : 0);

export default function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of Array.from(files).slice(0, 10)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        added.push({ file_id: data.file_id, mime: data.mime, filename: data.filename });
      }
      setForm((fm) => ({ ...fm, photos: [...(fm.photos || []), ...added] }));
      toast.success(`${added.length} photo(s) added`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/products", { params: { search } });
      setProducts(data);
    } catch (e) {}
  };

  useEffect(() => { load(); }, [search]);

  const save = async () => {
    try {
      if (editing) await api.put(`/products/${editing}`, form);
      else await api.post("/products", form);
      toast.success("Product saved");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Product Databank</h1>
          <div className="font-hand text-lg text-amber-800">quick info for your agents ✦</div>
        </div>
        <button data-testid="add-product-btn" className="btn-navy" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}><Plus size={15} /> Add Product</button>
      </div>

      <input data-testid="products-search-input" className="input-tactile max-w-sm mb-5" placeholder="Search by name or SKU..." value={search} onChange={(e) => setSearch(e.target.value)} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
        {products.map((p, i) => (
          <div key={p.id} className={`card-tactile p-4 ${["card-yellow", "card-green", "card-pink", "card-blue"][i % 4]}`} data-testid={`product-item-${p.id}`}>
            {(p.photos?.length > 0 || p.image_url) && (
              <div className="relative">
                <img src={p.photos?.length ? fileUrl(p.photos[0].file_id) : p.image_url} alt={p.name} className="w-full h-32 object-cover rounded-lg mb-2 border-2 bg-white" style={{ borderColor: "var(--cd-navy)" }} />
                {p.photos?.length > 1 && <span className="absolute top-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white border" style={{ borderColor: "var(--cd-navy)" }}>+{p.photos.length - 1} photos</span>}
              </div>
            )}
            <div className="font-heading font-bold">{p.name}</div>
            <div className="flex items-center justify-between mt-1">
              <span className="font-bold">{p.price ? `₹${p.price}` : "—"}</span>
              <span className={`status-badge ${p.stock_status === "in_stock" ? "badge-approved" : "badge-rejected"}`}>{p.stock_status === "in_stock" ? "in stock" : "out"}</span>
            </div>
            {p.sku && <div className="text-[10px] font-mono2 text-slate-600 mt-0.5">SKU: {p.sku}</div>}
            {p.description && <div className="text-xs text-slate-700 mt-1 line-clamp-2">{p.description}</div>}
            <div className="flex gap-1 mt-2">
              <button data-testid={`edit-product-${p.id}`} className="btn-ghost2 py-1 bg-white/60" onClick={() => { setForm({ ...EMPTY, ...p }); setEditing(p.id); setOpen(true); }}><Pencil size={13} /> Edit</button>
              <button data-testid={`delete-product-${p.id}`} className="btn-ghost2 py-1 text-red-600 bg-white/60" onClick={() => remove(p.id)}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="font-hand text-xl text-amber-800 col-span-full">no products yet — build your databank so agents can answer instantly</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-tactile border-2 max-h-[85vh] overflow-y-auto" style={{ borderColor: "var(--cd-navy)" }}>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <label className="label-upper">Name</label>
          <input data-testid="product-name-input" className="input-tactile" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-upper mt-1">Price (₹)</label>
              <input data-testid="product-price-input" className="input-tactile" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="label-upper mt-1">SKU</label>
              <input data-testid="product-sku-input" className="input-tactile" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </div>
          </div>
          <label className="label-upper mt-1">Photos (agents can drag these into chats)</label>
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="product-photos-input" onChange={(e) => uploadPhotos(e.target.files)} />
          <div className="flex flex-wrap gap-2">
            {(form.photos || []).map((ph) => (
              <div key={ph.file_id} className="relative">
                <img src={fileUrl(ph.file_id)} alt={ph.filename} className="w-16 h-16 object-cover rounded-lg border-2 bg-white" style={{ borderColor: "var(--cd-navy)" }} />
                <button data-testid={`remove-photo-${ph.file_id}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center" onClick={() => setForm((fm) => ({ ...fm, photos: fm.photos.filter((x) => x.file_id !== ph.file_id) }))}><X size={11} /></button>
              </div>
            ))}
            <button data-testid="add-photos-btn" className="w-16 h-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-[10px] font-bold gap-0.5" style={{ borderColor: "var(--cd-navy)" }} onClick={() => photoRef.current?.click()} disabled={uploading}>
              <ImagePlus size={16} /> {uploading ? "..." : "Add"}
            </button>
          </div>
          <label className="label-upper mt-1">Image URL (optional fallback)</label>
          <input data-testid="product-image-input" className="input-tactile" placeholder="https://..." value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          <label className="label-upper mt-1">Stock</label>
          <select data-testid="product-stock-select" className="input-tactile" value={form.stock_status} onChange={(e) => setForm({ ...form, stock_status: e.target.value })}>
            <option value="in_stock">In stock</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
          <label className="label-upper mt-1">Description <span className={wc(form.description) > 1000 ? "text-red-600" : "text-slate-400"}>({wc(form.description)}/1000 words)</span></label>
          <textarea data-testid="product-desc-input" className="input-tactile" rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <label className="label-upper mt-1">FAQs / Quick Info for agents <span className={wc(form.faqs) > 1000 ? "text-red-600" : "text-slate-400"}>({wc(form.faqs)}/1000 words)</span></label>
          <textarea data-testid="product-faqs-input" className="input-tactile" rows={5} placeholder="Q: Washable? A: Yes, machine wash cold..." value={form.faqs || ""} onChange={(e) => setForm({ ...form, faqs: e.target.value })} />
          <button data-testid="product-save-btn" className="btn-navy justify-center mt-2" disabled={!form.name || uploading || wc(form.description) > 1000 || wc(form.faqs) > 1000} onClick={save}>Save Product</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
